import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import {Link, useNavigate} from "react-router-dom";
import {Dropdown, DropdownToggle, DropdownMenu, Row, Col} from "reactstrap";
import SimpleBar from "simplebar-react";
import moment from "moment";
import {withTranslation} from "react-i18next";
import NotificationSound from "../../../assets/new-notification-022-370046.mp3";

function getTokenFromLocalStorage() {
    return localStorage.getItem("token"); // поправь под свой ключ
}

export async function apiFetch(path, {method = "GET", body, signal} = {}) {
    const token = getTokenFromLocalStorage();

    const res = await fetch(`${process.env.REACT_APP_SERVER_URL}${path}`, {
        method,
        signal,
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: body ? JSON.stringify(body) : undefined,
    });

    const data = await res.json().catch(() => null);

    if (!res.ok) {
        const msg = data?.message || data?.error?.message || `HTTP_${res.status}`;
        throw new Error(msg);
    }

    return data;
}

const POLL_MS = 15_000;

export function truncateText(text, maxLength = 100) {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength).trim() + "…";
}

const NotificationDropdown = (props) => {
    const [menu, setMenu] = useState(false);
    let navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const [loadingList, setLoadingList] = useState(false);
    const [busyMark, setBusyMark] = useState(false);
    const [error, setError] = useState("");

    const abortRef = useRef(null);
    const inFlightRef = useRef(false);
    const intervalRef = useRef(null);

    // звук
    const audioRef = useRef(null);
    const soundArmedRef = useRef(false);
    const prevUnreadRef = useRef(0);
    const didInitRef = useRef(false); // чтобы не играть на первом fetch

    const skip = 0;
    const limit = 50;

    // --- init audio + arm after first user interaction ---
    useEffect(() => {
        audioRef.current = new Audio(NotificationSound);
        audioRef.current.preload = "auto";

        const armSound = () => {
            soundArmedRef.current = true;
            document.removeEventListener("click", armSound);
            document.removeEventListener("keydown", armSound);
        };

        document.addEventListener("click", armSound);
        document.addEventListener("keydown", armSound);

        return () => {
            document.removeEventListener("click", armSound);
            document.removeEventListener("keydown", armSound);
        };
    }, []);

    const stopNetwork = useCallback(() => {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = null;
        inFlightRef.current = false;
    }, []);

    const playSoundIfNew = useCallback((nextUnreadCount) => {
        if (!didInitRef.current) return; // первый fetch — без звука
        if (!soundArmedRef.current) return;

        if (nextUnreadCount > prevUnreadRef.current) {
            try {
                if (audioRef.current) {
                    audioRef.current.currentTime = 0;
                    audioRef.current.play().catch(() => {
                    });
                }
            } catch (_) {
            }
        }
    }, []);

      useEffect(() => {
        if (!Array.isArray(items)) {
          setUnreadCount(0);
          return;
        }

        const count = items.filter((n) => n.status === "UNREAD").length;
        setUnreadCount(count);
      }, [items]);

    const fetchUnreadList = useCallback(
        async ({silent = false, forceUpdateList = false} = {}) => {
            if (inFlightRef.current) return; // защита от параллельных запросов
            inFlightRef.current = true;

            if (!silent) {
                setError("");
                setLoadingList(true);
            }

            if (abortRef.current) abortRef.current.abort();
            const ac = new AbortController();
            abortRef.current = ac;

            try {
                const data = await apiFetch(
                    `/api2/notification/all/${skip}/${limit}`,
                    {signal: ac.signal}
                );

                const list =
                    data?.success && Array.isArray(data.result) ? data.result : [];

                const nextUnread = list.length;

                // звук (только если реально выросло)
                playSoundIfNew(nextUnread);


                const count = list.filter((n) => n.status === "UNREAD").length;

                setUnreadCount(count);

                if (menu || forceUpdateList || !silent) {
                    setItems(list);
                }

                if (!silent) setError("");

                // обновляем prevUnread и ставим что init уже был
                prevUnreadRef.current = nextUnread;
                didInitRef.current = true;
            } catch (e) {
                if (e?.name === "AbortError") return;
                if (!silent) setError(e.message || "Failed to load notifications");
                // unreadCount не трогаем при ошибке (оставляем последнее)
            } finally {
                if (!silent) setLoadingList(false);
                inFlightRef.current = false;
            }
        },
        [limit, menu, playSoundIfNew, skip]
    );

    // --- polling (каждые 10 секунд) ---
    useEffect(() => {
        // 1) сразу загрузить badge
        fetchUnreadList({silent: true});

        // 2) polling интервал
        intervalRef.current = setInterval(() => {
            // если вкладка скрыта — не дергаем сеть
            if (document.visibilityState !== "visible") return;
            fetchUnreadList({silent: true});
        }, POLL_MS);

        // 3) при возврате во вкладку — обновить сразу
        const onVis = () => {
            if (document.visibilityState === "visible") {
                fetchUnreadList({silent: true});
            }
        };
        document.addEventListener("visibilitychange", onVis);

        return () => {
            document.removeEventListener("visibilitychange", onVis);
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = null;
            stopNetwork();
        };
    }, [fetchUnreadList, stopNetwork]);

    // когда открыли меню — обновляем список сразу (с лоадером)
    useEffect(() => {
        if (menu) fetchUnreadList({silent: false, forceUpdateList: true});
    }, [menu, fetchUnreadList]);

    // ---- actions ----


    const titleByType = useCallback(
        (n) => {
            switch (n.type) {
                case "HANDOFF":
                    return props.t("New handoff");
                case "COMMENT":
                    return props.t("New comment");
                case "STATUS_CHANGE":
                    return props.t("Status changed");
                case "UPDATE":
                default:
                    return props.t("Update");
            }
        },
        [props]
    );


    const listToRender = useMemo(() => items, [items]);


    const handleClickUnread = async (e, notificationId, flowDocId) => {
        e.preventDefault();
        e.stopPropagation();
        if (busyMark) return;

        setError("");
        setBusyMark(true);

        // ✅ optimistic update: пометили один элемент прочитанным
        setItems((prev) =>
            prev.map((n) =>
                String(n._id) === String(notificationId)
                    ? {...n, status: 'READ'}
                    : n
            )
        );

        try {
            await apiFetch(`/api2/notification/mark-as-read/${notificationId}`, {method: "GET"});
            navigate(`/detail-logistic/${flowDocId}`);
        } catch (e2) {
            setError(e2.message || "Failed to mark all as read");
        } finally {
            setBusyMark(false);
        }
    };


    return (
        <Dropdown
            isOpen={menu}
            toggle={() => setMenu((v) => !v)}
            className="dropdown d-inline-block"
            tag="li"
        >
            <DropdownToggle
                className="btn header-item noti-icon waves-effect"
                tag="button"
                id="page-header-notifications-dropdown"
            >
                <i className="uil-bell"></i>

                {unreadCount > 0 && (
                    <span className="badge bg-danger rounded-pill">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
                )}
            </DropdownToggle>

            <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
                <div className="p-3">
                    <Row className="align-items-center">
                        <Col>
                            <h6 className="m-0 font-size-16">შეტყობინებები</h6>
                        </Col>

                        <div className="col-auto">
                            {/*                <Link
                    to="#!"
                    className="small"
                    onClick={markAllAsRead}
                    style={{
                      pointerEvents: busyMark ? "none" : "auto",
                      opacity: busyMark ? 0.6 : 1,
                    }}
                >
                  {busyMark ? "..." : "Mark all as read"}
                </Link>*/}
                        </div>
                    </Row>

                    {error ? <div className="mt-2 small text-danger">{error}</div> : null}
                </div>

                <SimpleBar style={{height: "230px"}}>
                    {loadingList ? (
                        <div className="p-3 text-muted small">{props.t("Loading...")}</div>
                    ) : null}

                    {!loadingList && listToRender.length === 0 ? (
                        <div className="p-3 text-muted small">
                            ახალი შეტყობინებები არ არის
                        </div>
                    ) : null}

                    {listToRender.map((n) => {
                        const when = n.createdAt
                            ? moment(n.createdAt).format("YYYY-MM-DD HH:mm")
                            : "";
                        const isUnread = n.status;
                        return (
                            <div
                                className={[
                                    "notification-item",
                                    "text-dark",
                                    isUnread === 'UNREAD' ? "notification-unread" : "notification-read",
                                ].join(" ")}
                                key={String(n._id)}
                                // to={`/detail-logistic/${n.flowDocId}`}
                                onClick={(e) => handleClickUnread(e, n._id, n.flowDocId)}
                            >
                                <div className="d-flex align-items-start">
                                    <div className="avatar-xs me-3">
                                        {
                                            n.type === 'HANDOFF' &&
                                            <span className="avatar-title bg-primary rounded-circle font-size-16">
                          <i className="uil-bell"></i>
                        </span>
                                        }
                                        {
                                            n.type === 'COMMENT' &&
                                            <span className="avatar-title bg-success rounded-circle font-size-16">
                    <i className="bx bx-badge-check"/>
                  </span>
                                        }
                                    </div>

                                    <div className="flex-1">
                                        <h6 className="mt-0 mb-1">
                                            {n.commentary || titleByType(n)}
                                        </h6>

                                        <div className="font-size-12 text-muted">
                                            {n.actorUserName ? (
                                                <p className="mb-1">{n.actorUserName} - {n.flowDocCompanyName} - {n.flowDocId}</p>
                                            ) : null}

                                            {
                                                n.type === 'COMMENT' && <p className="mb-0">
                                                    <i>{truncateText(n.message, 100)}</i>
                                                </p>
                                            }

                                            <p className="mb-0">
                                                <i className="mdi mdi-clock-outline"></i>{" "}
                                                <span>{when}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}


                </SimpleBar>

                <div className="p-2 border-top d-grid">
                    <Link
                        className="btn btn-sm btn-link font-size-14 text-center"
                        to="/notifications"
                    ><i className="uil-arrow-circle-right me-1"></i>
                        {" "}
                        ყველას ნახვა
                    </Link>
                </div>

            </DropdownMenu>
        </Dropdown>
    );
};

export default withTranslation()(NotificationDropdown);

NotificationDropdown.propTypes = {
    t: PropTypes.any,
};
